// Lightweight attribution-source helper.
//
// Goal:
//   - Capture `?source=...` from any landing page (especially /free) so we
//     can persist it through the multi-page register flow.
//   - sessionStorage is the right scope — it survives the /free → /register
//     navigation but doesn't follow the user forever, which keeps the data
//     honest (no stale tags weeks later).
//   - Sanitize aggressively so we never round-trip strange characters into
//     the backend, logs, or future CSV exports.
//
// Used by:
//   - pages/FreeLanding.jsx — captureSourceFromUrl() on mount
//   - pages/Register.jsx     — readStoredSource() at submit
//
// Storage key is namespaced so it can't collide with other Tasklio storage.

const STORAGE_KEY = "tasklio_acq_source";
const MAX_LEN = 64;

// Keep only URL-safe alphanumerics + `_` / `-`. Anything outside that —
// whitespace, punctuation, unicode — is dropped so the resulting string is
// trivially safe for analytics joins, logs, and querystrings.
export const sanitizeSource = (raw) => {
  if (!raw) return null;
  const cleaned = String(raw).trim().toLowerCase().replace(/[^a-z0-9_-]/g, "");
  if (!cleaned) return null;
  return cleaned.slice(0, MAX_LEN);
};

// Reads `?source=...` from the current location and stores it in
// sessionStorage. Idempotent — repeat calls with the same source are no-ops.
// Returns the captured value (or null if nothing was found).
export const captureSourceFromUrl = () => {
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("source") || params.get("utm_source");
    const cleaned = sanitizeSource(raw);
    if (cleaned) {
      window.sessionStorage.setItem(STORAGE_KEY, cleaned);
    }
    return cleaned;
  } catch {
    // sessionStorage can throw in private-mode Safari; swallow it — losing
    // attribution is never worth breaking the page.
    return null;
  }
};

// Read whatever source was captured earlier (or null). Safe to call from any
// page; we don't auto-clear it because the user might bounce around between
// /free, /pricing, and /register before submitting.
export const readStoredSource = () => {
  if (typeof window === "undefined") return null;
  try {
    return sanitizeSource(window.sessionStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
};

// Called by the success handler in Register so a refresh of /register
// doesn't re-tag the next signup with a stale source.
export const clearStoredSource = () => {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
};
